// Route: /tests/edit/$id — редактор теста.
// См. docs/ui-spec/quizzes.edit.[id].{desktop,mobile}.md.

import { FORM_PAGE_SHELL, MagicMomentLoader } from "@studyhub/ui";
import { Button } from "@studyhub/ui/components/button";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@studyhub/ui/components/tabs";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { toast } from "@studyhub/ui/toast";
import { IconArrowLeft, IconCheck, IconFolderPlus, IconLoader2 } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddToCollectionMenu } from "@/components/library/add-to-collection-menu";
import { useQuiz, useUpdateQuiz } from "@/components/quizzes/_api";
import { QuestionsList } from "@/components/quizzes/edit/questions-list";
import { QuizMetadata, type QuizMetadataDraft } from "@/components/quizzes/edit/quiz-metadata";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { useSocketGenerationProgress } from "@/hooks/use-socket-generation-progress";

export const Route = createFileRoute("/tests/edit/$id")({
	component: QuizEditPage,
});

function QuizEditPage(): React.ReactElement {
	const { t } = useTranslation("quizzes");
	const { t: tc } = useTranslation("library");
	const { t: tCommon } = useTranslation("common");
	const { id } = Route.useParams();
	const query = useQuiz(id);
	const updateQuiz = useUpdateQuiz(id);
	const qc = useQueryClient();
	useArtifactWatching(markArtifactSeenKey("quiz", id));

	const [draft, setDraft] = useState<QuizMetadataDraft | null>(null);
	const [genFailed, setGenFailed] = useState(false);

	// AI-генерация в процессе: quiz существует, но questions ещё нет.
	// Inngest пишет вопросы пачкой после Step 3 — до этого rendering должен
	// показывать прелоадер, а не «Добавь первый вопрос».
	// `genFailed` (из Socket.IO `quiz:generation_failed`) останавливает прелоадер:
	// у `tests` нет колонки generationStatus, поэтому без этого сигнала
	// `isGenerating` (generatedByAI && questions=[]) висел бы вечно при провале.
	const isGenerating = useMemo(() => {
		if (!query.data || genFailed) return false;
		return query.data.quiz.generatedByAI && query.data.questions.length === 0;
	}, [query.data, genFailed]);

	const quizProgress = useSocketGenerationProgress({
		event: "quiz:generation_progress",
		match: (payload) => payload.quizId === id || payload.jobId === id,
		enabled: isGenerating,
		defaultCrawlMs: 40_000,
	});

	// Socket.IO event `quiz:ready` (apps/server/src/inngest/functions/generateQuiz.ts
	// Step 5) триггерит invalidate — questions сразу подтянутся.
	const invalidateQuiz = useCallback(
		(payload: { quizId: string }) => {
			if (payload.quizId !== id) return;
			setGenFailed(false);
			void qc.invalidateQueries({ queryKey: ["quizzes", "get", id] });
		},
		[id, qc],
	);
	useSocketEvent("quiz:ready", invalidateQuiz);

	// Socket.IO event `quiz:generation_failed` (generateQuiz.ts: source-too-short /
	// no-valid-questions / onFailure после retries). Сервер эмитит, клиент обязан
	// прекратить бесконечный прелоадер и показать ошибку (контракт events.ts).
	const handleQuizGenFailed = useCallback(
		(payload: { quizId: string | null; errorMessage: string }) => {
			if (payload.quizId !== id) return;
			setGenFailed(true);
			toast.error(payload.errorMessage || t("list.errorTitle"));
		},
		[id, t],
	);
	useSocketEvent("quiz:generation_failed", handleQuizGenFailed);

	// Fallback polling: если socket events потеряны (reconnect, no realtime) —
	// раз в 3 сек refetch пока генерация не завершилась.
	// query.refetch читаем через ref — `query` объект пересоздаётся на каждый
	// TanStack Query рендер; включение его в deps сбрасывало бы interval.
	const refetchRef = useRef(query.refetch);
	useEffect(() => {
		refetchRef.current = query.refetch;
	}, [query.refetch]);
	useEffect(() => {
		if (!isGenerating) return;
		const interval = setInterval(() => void refetchRef.current(), 3000);
		return () => clearInterval(interval);
	}, [isGenerating]);

	const serverMeta = useMemo<QuizMetadataDraft | null>(() => {
		if (!query.data) return null;
		const q = query.data.quiz;
		return {
			name: q.name,
			description: q.description ?? "",
			timeLimit: q.timeLimit,
			passingScore: q.passingScore,
		};
	}, [query.data]);

	useEffect(() => {
		if (serverMeta) setDraft(serverMeta);
	}, [serverMeta]);

	const isDirty = useMemo(
		() =>
			draft != null && serverMeta != null && JSON.stringify(draft) !== JSON.stringify(serverMeta),
		[draft, serverMeta],
	);

	async function handleSave(): Promise<void> {
		if (!draft || !serverMeta) return;
		const patch: Partial<QuizMetadataDraft> = {};
		if (draft.name !== serverMeta.name) patch.name = draft.name;
		if (draft.description !== serverMeta.description) patch.description = draft.description;
		if (draft.timeLimit !== serverMeta.timeLimit) patch.timeLimit = draft.timeLimit;
		if (draft.passingScore !== serverMeta.passingScore) patch.passingScore = draft.passingScore;
		if (Object.keys(patch).length === 0) return;
		try {
			await updateQuiz.mutateAsync(patch);
			toast.success(t("edit.saved"));
		} catch {
			toast.error(t("list.errorTitle"));
		}
	}

	if (query.isLoading) {
		return (
			<div className={FORM_PAGE_SHELL}>
				<Skeleton className="h-8 w-1/2" />
				<Skeleton className="h-48 w-full" />
				<Skeleton className="h-24 w-full" />
			</div>
		);
	}

	if (query.isError || !query.data) {
		return (
			<div className={FORM_PAGE_SHELL}>
				<Button asChild variant="ghost" className="cursor-pointer">
					<Link to="/tests">
						<IconArrowLeft size={14} />
						{t("edit.backToList")}
					</Link>
				</Button>
			</div>
		);
	}

	const { quiz, questions } = query.data;

	return (
		<div className={FORM_PAGE_SHELL}>
			<header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
				<div className="min-w-0">
					<Button asChild variant="ghost" size="sm" className="cursor-pointer -ml-2">
						<Link to="/tests">
							<IconArrowLeft size={14} />
							<span className="ml-1">{t("edit.backToList")}</span>
						</Link>
					</Button>
					<div className="mt-1 flex items-start gap-2">
						<h1 className="line-clamp-2 break-words text-xl font-semibold sm:text-2xl">
							{quiz.name}
						</h1>
					</div>
				</div>
				{/* Mobile: действия отдельной строкой full-width стопкой. */}
				<div className="flex items-center gap-2 max-sm:w-full sm:shrink-0">
					<IconTooltip label={tc("addTo.trigger")}>
						<AddToCollectionMenu
							itemType="test"
							itemId={quiz.id}
							trigger={
								<Button
									variant="outline"
									size="icon"
									className="cursor-pointer pointer-coarse:size-11"
									aria-label={tc("addTo.trigger")}
								>
									<IconFolderPlus size={16} />
								</Button>
							}
						/>
					</IconTooltip>
					{isGenerating ? (
						<Button disabled className="cursor-not-allowed max-sm:flex-1 max-sm:h-11">
							<IconLoader2 size={14} className="mr-1 animate-spin" />
							{t("edit.generating")}
						</Button>
					) : (
						<Button asChild className="cursor-pointer max-sm:flex-1 max-sm:h-11">
							<Link to="/tests/take/$id" params={{ id: quiz.id }}>
								{t("take.next")}
							</Link>
						</Button>
					)}
					<Button
						variant="outline"
						className="cursor-pointer max-sm:flex-1 max-sm:h-11"
						disabled={!isDirty || updateQuiz.isPending || isGenerating}
						onClick={() => void handleSave()}
					>
						{updateQuiz.isPending ? (
							<IconLoader2 size={14} className="mr-1 animate-spin" />
						) : (
							<IconCheck size={14} className="mr-1" />
						)}
						{t("edit.saveChanges")}
					</Button>
				</div>
			</header>

			<Tabs defaultValue="questions">
				<TabsList>
					<TabsTrigger value="questions" className="cursor-pointer">
						{t("edit.questions")}
					</TabsTrigger>
					<TabsTrigger value="metadata" className="cursor-pointer">
						{t("edit.metadata")}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="questions" className="mt-4">
					{isGenerating ? (
						<MagicMomentLoader
							title={t("edit.generatingTitle")}
							tip={t("edit.generatingHint")}
							steps={[
								{
									label: t("edit.generating"),
									status: "in-progress",
								},
								{
									label: t("edit.questions"),
									status: "pending",
								},
							]}
							progress={quizProgress.progress}
							progressLabel={tCommon("generation.progress", {
								percent: quizProgress.percent,
								defaultValue: `${quizProgress.percent}%`,
							})}
							etaLabel={quizProgress.etaLabel ?? undefined}
							className="border-dashed"
						/>
					) : genFailed && questions.length === 0 ? (
						<div className="rounded-lg border border-dashed border-destructive/40 p-10 flex flex-col items-center justify-center gap-2 text-center">
							<p className="font-medium text-destructive">{t("edit.generationFailedTitle")}</p>
							<p className="text-sm text-muted-foreground">{t("edit.generationFailedHint")}</p>
						</div>
					) : (
						<QuestionsList quizId={quiz.id} questions={questions} />
					)}
				</TabsContent>
				<TabsContent value="metadata" className="mt-4">
					{draft && (
						<QuizMetadata
							quiz={quiz}
							value={draft}
							onChange={(patch) => setDraft((prev) => (prev ? { ...prev, ...patch } : prev))}
						/>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
